// Me tab → Notifications: link this account's Telegram, and send a test push
// (in any of the notification sounds) to check the whole path end to end.
//
// Telegram replaces WhatsApp for every home alert (WhatsApp is chargeable), so a
// customer who never links Telegram gets push only. Linking is one tap: the
// server mints a signed t.me/<bot>?start=SL… link, Telegram opens, the user taps
// Start, and the bot's poller (on the attendance box — one poller per bot)
// forwards the code back to our server. See Home Automation api/new/notify.js.
//
// The result of a test is reported as the server counted it — sent / no token /
// not linked — rather than "Sent!", because a push to a phone with no token, or
// a Telegram message to an unlinked chat, is not an error anywhere and would
// otherwise look like success.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  MenuItem, TextField, CircularProgress, useTheme,
} from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import { useApp } from '../../../core/state/AppContext';
import { openExternal } from '../../../core/constants';
import { isNativePlatform } from '../../../core/platform';
import { soundOptions } from '../../../core/push/pushChannels';
import {
  getTelegramLinkInfo, unlinkTelegram, sendTestNotification,
} from '../../../core/api/notifyApi';

export default function NotificationsDialog({ open, onClose }) {
  const theme = useTheme();
  const c = theme.sli;
  const { realUserId } = useApp();

  const [info, setInfo] = useState(undefined);   // undefined loading, null unavailable
  const [sound, setSound] = useState('default');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');

  const load = useCallback(async () => {
    setInfo(undefined);
    setInfo(await getTelegramLinkInfo(realUserId));
  }, [realUserId]);

  useEffect(() => { if (open) { setResult(''); load(); } }, [open, load]);

  const unlink = async () => {
    setBusy(true);
    try { await unlinkTelegram(realUserId); await load(); } catch { setResult('Could not unlink. Try again.'); }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    setResult('');
    try {
      const r = await sendTestNotification(realUserId, { sound });
      if (!r) {
        setResult('The server has not been updated for notifications yet.');
      } else {
        const p = r.push || {};
        const t = r.telegram || {};
        const push = p.sent ? 'Push sent' : (p.noToken ? 'No push: this account has no phone registered' : 'Push failed');
        const tg = t.sent ? 'Telegram sent' : (t.notLinked ? 'Telegram not linked' : 'Telegram failed');
        setResult(`${push}. ${tg}.`);
      }
    } catch {
      setResult('Could not reach the server.');
    }
    setBusy(false);
  };

  const text = { color: c.text };
  const dim = { color: `${c.text}B3`, fontSize: 13 };

  return (
    <Dialog open={open} onClose={onClose} fullWidth
      PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px' } }}>
      <DialogTitle sx={{ ...text, fontWeight: 700 }}>Notifications</DialogTitle>
      <DialogContent>
        <Typography sx={{ ...text, fontWeight: 700, mb: 0.5 }}>Telegram</Typography>
        {info === undefined && <CircularProgress size={20} />}
        {info === null && (
          <Typography sx={dim}>Telegram linking is not available yet — the server needs its update.</Typography>
        )}
        {info && info.configured === false && (
          <Typography sx={dim}>Telegram is not set up on the server yet.</Typography>
        )}
        {info && info.configured !== false && (
          <Box>
            <Typography sx={dim}>
              {info.linked
                ? 'Linked. Home alerts arrive in Telegram as well as here.'
                : 'Not linked. Link Telegram to get home alerts there too — they no longer come on WhatsApp.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
              {!info.linked && (
                <Button variant="contained" startIcon={<TelegramIcon />} disabled={busy}
                  onClick={() => openExternal(info.link)}>
                  Link Telegram
                </Button>
              )}
              {info.linked && (
                <Button variant="outlined" disabled={busy} onClick={unlink}>Unlink</Button>
              )}
              <Button disabled={busy} onClick={load}>Check again</Button>
            </Box>
            {!info.linked && (
              <Typography sx={{ ...dim, mt: 1 }}>
                Telegram opens — tap <b>Start</b>, then come back and tap Check again.
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ height: '1px', bgcolor: `${c.text}22`, my: 2 }} />

        <Typography sx={{ ...text, fontWeight: 700, mb: 0.5 }}>Test notification</Typography>
        {!isNativePlatform() && (
          <Typography sx={{ ...dim, mb: 1 }}>
            Push arrives on the phone app, not in this browser.
          </Typography>
        )}
        <TextField
          select fullWidth size="small" label="Sound" value={sound}
          onChange={(e) => setSound(e.target.value)}
          sx={{ mt: 1, '& .MuiInputBase-input': text, '& .MuiInputLabel-root': dim }}
        >
          {soundOptions().map((s) => <MenuItem key={s.sound} value={s.sound}>{s.name}</MenuItem>)}
        </TextField>
        <Button variant="contained" sx={{ mt: 1.5 }} disabled={busy} onClick={test}>
          {busy ? <CircularProgress size={18} /> : 'Send test'}
        </Button>
        {result && <Typography sx={{ ...text, fontSize: 14, mt: 1.25 }}>{result}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={text}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
